import CustomModal from '@components/CustomModal';
import CreateEditOrganizationModal from '@components/modals/CreateEditOrganizationModal';
import FeedbackModal from '@components/modals/FeedbackModal';
import WolfAvatar from '@components/ui/WolfAvatar';
import { graphQlClient, updateHeaders } from '@config/graphqlClient';
import OrganizationsGraphQL from '@graphql/organization.queries';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { createOrUpdateOrganizationRequest, OrganizationStatus } from '@models/organization.model';
import { getSelfWithOrganizationRequest, OrganizationSummary, UserRoles } from '@models/user.model';
import { Box, Divider, IconButton, Menu, MenuItem, styled, Typography } from '@mui/material';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { clearBadges } from '@redux/reducers/badges.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import { setUserDetails, setUserOrganizations } from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import { useMemo, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import WolfTooltip from './ui/WolfTooltip';

const YellowDot = styled(Box)(({ theme }) => ({
  width: '15px',
  height: '15px',
  borderRadius: '50%',
  backgroundColor: theme.palette.warning.main,
  position: 'absolute',
  bottom: 45,
  right: 5,
}));

interface AvatarDropdownProps {
  avatar: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

const REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
let lastOrganizationRefresh = 0;

const AvatarDropdown: React.FC<AvatarDropdownProps> = ({ avatar, firstName, lastName, email }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const open = Boolean(anchorEl);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const localeMenu = useTranslation('menu');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const { role, organizations } = useSelector((state: RootState) => state.user);
  const { organizationId, lastProfileUpdate } = useSelector((state: RootState) => state.organization);

  const handleReportIssue = () => {
    setShowFeedbackModal(true);
  };

  const options = [
    { text: localeMenu['settings'], link: '/settings' },
    { text: localeMenu['reportIssue'], link: '', action: handleReportIssue },
    { text: localeMenu['logout'], link: '/logout' },
  ];

  if ([UserRoles.OWNER, UserRoles.ADMIN, UserRoles.SUPER_ADMIN].includes(role as UserRoles)) {
    options.unshift({ text: localeMenu['myOrganization'], link: '/organization/' + organizationId });
    options.push({ text: localeMenu['integrations'], link: `/integrations` });

  }

  const handleClick = async (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    // Refresh organization list when dropdown opens (debounced to 5 min)
    const now = Date.now();
    if (now - lastOrganizationRefresh < REFRESH_INTERVAL_MS) {
      return;
    }

    try {
      const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
        UserGraphQL.queries.getSelfWithOrganization,
      );
      const organizations = userData.getSelfWithOrganization.organizations || [];
      dispatch(setUserOrganizations(organizations));
      lastOrganizationRefresh = now;
    } catch (error) {
      console.error('Error refreshing organizations:', error);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (option: typeof options[0]) => {
    if (option.action) {
      option.action();
    } else if (option.link) {
      history.push(option.link);
    }
    handleClose();
  };

  const handleOrganizationSwitch = async (org: OrganizationSummary) => {
    if (org.organizationId === organizationId) {
      handleClose();
      return;
    }

    try {
      // Step 1: Update GraphQL client headers to use new organization context
      updateHeaders({
        'X-Organization-Id': org.organizationId,
      });

      // Step 2: Fetch fresh user data with the new organization context to get correct role
      const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
        UserGraphQL.queries.getSelfWithOrganization,
      );

      const user = userData.getSelfWithOrganization.user;
      const role = userData.getSelfWithOrganization.role;
      const organization = userData.getSelfWithOrganization.organization;
      const organizations = userData.getSelfWithOrganization.organizations || [];

      // Step 3: Batch ALL Redux updates to trigger only one re-render
      batch(() => {
        if (organization) {
          dispatch(setOrganizationData(organization));
        }
        dispatch(setUserDetails({ ...user, role }));
        dispatch(setUserOrganizations(organizations));
        // Reset badge counts so they get refetched for the new org
        dispatch(clearBadges());
        dispatch(
          appendActionMessage({
            message: `${localeActionMessages['switchedToOrganization']}: ${organization.name}`,
            type: SnackType.SUCCESS,
          }),
        );
      });

      // Step 4: Clear the header override so future requests use Redux state
      updateHeaders({});

      // Step 5: Navigate to processes page and clear any query params
      // Query params like serviceId are org-specific and must be cleared
      if (location.pathname !== '/processes' || location.search) {
        history.push('/processes');
      }
    } catch (error) {
      console.error('Error switching organization:', error);

      // Clear the header override on error
      updateHeaders({});

      dispatch(
        appendActionMessage({
          message: localeActionMessages['failedToSwitchOrganization'],
          type: SnackType.ERROR,
        }),
      );
    }

    handleClose();
  };

  const createOrganizationByEmail = async (name: string, ownerEmail: string, description?: string, skipSeed?: boolean) => {
    if (!name || !ownerEmail) return;

    setSaving(true);
    try {
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.createOrganizationByEmail,
        {
          organization: {
            name: name,
            ownerEmail: ownerEmail,
            description: description,
            skipSeed: skipSeed,
          },
        },
      );

      if ((data as any)?.createOrganizationByEmail?.organizationId) {
        dispatch(
          appendActionMessage({
            message: localeActionMessages['organizationCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );

        // Refresh user data to get the new organization
        const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
          UserGraphQL.queries.getSelfWithOrganization,
        );

        const user = userData.getSelfWithOrganization.user;
        const userRole = userData.getSelfWithOrganization.role;
        const organizations = userData.getSelfWithOrganization.organizations || [];

        // Batch Redux updates to trigger only one re-render
        batch(() => {
          dispatch(setUserDetails({ ...user, role: userRole }));
          dispatch(setUserOrganizations(organizations));
        });

        // Automatically switch to the new organization
        const newOrg = organizations.find(
          (org) => org.organizationId === (data as any).createOrganizationByEmail.organizationId,
        );
        if (newOrg) {
          await handleOrganizationSwitch(newOrg);
        }

        setShowCreateModal(false);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  const showYellowDot = useMemo(() => {
    if (!lastProfileUpdate) return false;
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    return new Date(lastProfileUpdate) < ninetyDaysAgo;
  }, [lastProfileUpdate]);

  // Filter out disabled organizations and sort alphabetically
  const activeOrganizations = useMemo(() => {
    return (
      organizations
        ?.filter((org) => org.status !== OrganizationStatus.DISABLED)
        .sort((a, b) => a.name.localeCompare(b.name)) || []
    );
  }, [organizations]);

  const displayName = firstName && lastName
    ? `${firstName} ${lastName}`.slice(0, 20) + (`${firstName} ${lastName}`.length > 20 ? '...' : '')
    : email
      ? email.slice(0, 20) + (email.length > 20 ? '...' : '')
      : 'Complete Profile';

  return (
    <Box className="flex items-center">
      <Box
        onClick={handleClick}
        className="flex items-center cursor-pointer"
        sx={{ '&:hover': { opacity: 0.8 } }}
      >
        <IconButton className="relative">
          <WolfAvatar size="48px" src={avatar || ''} />
          {/* Show yellow dot if lastProfileUpdate was more than 90 days ago */}
          <WolfTooltip title={localeMenu['updateProfile']}>
            <span>{showYellowDot && <YellowDot />}</span>
          </WolfTooltip>
        </IconButton>
        {(firstName || lastName || email) && (
          <WolfTooltip title={firstName && lastName ? `${firstName} ${lastName}` : email || 'Complete Profile'}>
            <Typography variant="body14" color="textSecondary" sx={{ textOverflow: 'ellipsis' }}>
              {displayName}
            </Typography>
          </WolfTooltip>
        )}
      </Box>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}>
        <Box className="shadow-md bg-backgroundUI">
          {/* Organization switcher section */}
          {activeOrganizations && activeOrganizations.length > 1 && (
            <>
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body12" color="textSecondary">
                  {localeMenu['switchOrganization']}
                </Typography>
              </Box>
              {activeOrganizations.map((org) => (
                <MenuItem
                  key={org.organizationId}
                  onClick={() => handleOrganizationSwitch(org)}
                  selected={org.organizationId === organizationId}>
                  <Box>
                    <Typography variant="body14" sx={{ fontWeight: org.organizationId === organizationId ? 600 : 400 }}>
                      {org.name}
                    </Typography>
                    {/* {org.description && (
                      <Typography variant="body12" color="textSecondary">
                        {org.description.length > 30 ? org.description.slice(0, 30) + '...' : org.description}
                      </Typography>
                    )} */}
                  </Box>
                </MenuItem>
              ))}
              <Divider />
            </>
          )}

          {/* Create Organization option */}
          <MenuItem
            onClick={() => {
              setShowCreateModal(true);
              handleClose();
            }}>
            <Typography variant="body14" color="primary" sx={{ fontWeight: 600 }}>
              + {localeMenu['createOrganization']}
            </Typography>
          </MenuItem>
          <Divider />

          {/* Regular menu options */}
          {options.map((option) => (
            <MenuItem key={option.text} onClick={() => handleMenuItemClick(option)}>
              {option.text}
            </MenuItem>
          ))}
        </Box>
      </Menu>

      {/* Create Organization Modal */}
      <CustomModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title={localeMenu['createOrganization']}
        maxWidth="600px">
        <CreateEditOrganizationModal
          modalClose={() => setShowCreateModal(false)}
          createOrganizationByEmail={createOrganizationByEmail}
          saving={saving}
          useSimplifiedCreation={true}
          hideOwnerEmail={true}
        />
      </CustomModal>

      {/* Feedback Modal */}
      <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} />
    </Box>
  );
};

export default AvatarDropdown;
